import React from "react";
import { BlockStack, Text, Box } from "@shopify/polaris";
import { BarWidget, symbolFor, money, applyTokens } from "./BarWidget";
import StorefrontPageSkeleton from "./StorefrontPageSkeleton";

const PLACEMENT_LABELS = {
  product_page: "Product page preview",
  cart_page: "Cart page preview",
  home_page: "Home page preview",
  announcement_bar: "Announcement bar preview",
};

export { PLACEMENT_LABELS };

export default function ShippingBarPreview({
  content,
  design,
  upsell,
  placementType = "product_page",
  previewMode = "all",
}) {
  const messages = content?.messages || {};
  const goal = content?.goal || {};
  const sym = symbolFor(goal.currency || "USD");
  const goalAmount = Number(goal.amount ?? 50);
  const widgetWidth = design?.layout?.widget_width_percent ?? 100;

  const goalStr = money(sym, goalAmount);
  const remaining = Math.max(1, Math.round(goalAmount * 0.3));
  const progressStr = money(sym, remaining);
  const progressPct = Math.min(100, Math.round(((goalAmount - remaining) / goalAmount) * 100));

  const allStates = [
    {
      key: "initial",
      message: applyTokens(messages.initial || "Free shipping for orders over {order-value}!", goalStr, goalStr),
      pct: 4,
    },
    {
      key: "progress",
      message: applyTokens(
        messages.progress || "You're only {order-value-progress} away from free shipping!",
        goalStr,
        progressStr,
      ),
      pct: progressPct,
    },
    {
      key: "success",
      message: applyTokens(messages.success || "Congratulations! You've got free shipping!", goalStr, goalStr),
      pct: 100,
    },
  ];

  /** content = 3 message states in page skeleton; progress = single widget (upsell/design/placement) */
  const states =
    previewMode === "content"
      ? allStates
      : previewMode === "progress"
        ? allStates.filter((s) => s.key === "progress")
        : allStates;

  const showUpsellInPreview = previewMode === "progress";
  const previewUpsell = showUpsellInPreview ? upsell : { mode: "none", products: [] };

  const widgets = states.map((s) => (
    <BarWidget
      key={s.key}
      message={s.message}
      pct={s.pct}
      design={design}
      upsell={previewUpsell}
      sym={sym}
    />
  ));

  return (
    <div className="fsb-preview-wrap">
      <BlockStack gap="300">
        <StorefrontPageSkeleton placementType={placementType} widgetWidthPercent={widgetWidth}>
          {placementType === "announcement_bar" ? (
            <BarWidget
              message={states[0]?.message || allStates[1].message}
              pct={states[0]?.pct ?? progressPct}
              design={{
                ...design,
                card: {
                  ...design?.card,
                  corner_radius: 0,
                  spacing: { ...(design?.card?.spacing || {}), outside_top: 0, outside_bottom: 0 },
                },
              }}
              upsell={previewUpsell}
              sym={sym}
            />
          ) : previewMode === "content" ? (
            <div className="fsb-preview-widget-stack">{widgets}</div>
          ) : (
            widgets
          )}
        </StorefrontPageSkeleton>
        <Box paddingBlockStart="100">
          <Text as="p" variant="bodySm" tone="subdued" alignment="center">
            Live storefront styling may differ slightly from this preview.
          </Text>
        </Box>
      </BlockStack>
    </div>
  );
}
